/**
 * Dashboard Service
 * API operations for dashboard metrics
 */
import { apiClient } from './apiClient';
import { API_ENDPOINTS } from '@/constants/api';
import { getAvatarColor } from '@/constants/roles';
import type {
  DashboardMetricsApiResponse,
  DashboardMetricsData,
  DashboardSummaryApiResponse,
  TaskStatsApiResponse,
  TaskProgressApiResponse,
  RecentTasksApiResponse,
  DelayedTasksApiResponse,
} from '@/types/api/responses';
import type {
  PriorityData,
  PriorityLevel,
} from '@/pages/DashboardPage/components/PriorityRow';
import type { TeamMemberWorkload } from '@/pages/DashboardPage/components/TeamWorkloadSection';
import type {
  TaskStats,
  TaskChartDataPoint,
} from '@/pages/DashboardPage/components/TaskOverviewSection/TaskOverviewSection.types';
import type { RecentTask } from '@/pages/DashboardPage/components/CompactTaskRow/CompactTaskRow.types';
import type { DelayedTask } from '@/pages/DashboardPage/components/DelayedTaskRow/DelayedTaskRow.types';
import type { TaskStatus, Priority } from '@/types';

/** API priority name literals returned by the backend */
type ApiPriorityName = 'Urgent' | 'High' | 'Medium' | 'Low';

/** Maps API priority names to frontend priority keys */
const PRIORITY_NAME_MAP: Readonly<Record<ApiPriorityName, PriorityLevel>> = {
  Urgent: 'urgent',
  High: 'high',
  Medium: 'medium',
  Low: 'low',
};

/** Combined summary data with already-transformed domain types */
export interface DashboardSummaryData {
  readonly taskStats: TaskStats;
  readonly priorityDistribution: PriorityData[];
  readonly totalTasks: number;
  readonly teamWorkload: TeamMemberWorkload[];
}

function transformMetricsResponse(
  response: DashboardMetricsApiResponse
): DashboardMetricsData {
  return {
    totalWorkspaces: response.metrics.total_workspaces,
    totalTeamMembers: response.metrics.total_team_members,
    activeTasks: response.metrics.active_tasks,
    completedThisMonth: response.metrics.completed_this_month,
    generatedAt: response.generatedAt,
  };
}

function transformTaskStats(response: TaskStatsApiResponse): TaskStats {
  return {
    total: response.total,
    pending: response.pending,
    inProgress: response.inProgress,
    completed: response.completed,
    delayed: response.delayed,
  };
}

function transformPriorityDistribution(
  priorities: DashboardSummaryApiResponse['priorityDistribution']['priorities']
): PriorityData[] {
  return priorities.map((item) => ({
    priority: PRIORITY_NAME_MAP[item.priority as ApiPriorityName] ?? 'low',
    total: item.total,
    pending: item.pending,
    active: item.inProgress,
    completed: item.completed,
    delayed: item.delayed,
  }));
}

function transformTeamWorkload(
  members: DashboardSummaryApiResponse['teamWorkload']['members']
): TeamMemberWorkload[] {
  return members.map((member) => ({
    id: member.userId,
    name: member.displayName,
    avatarUrl: member.avatarUrl,
    avatarColor: getAvatarColor(member.userId),
    done: member.completedCount,
    active: member.activeCount,
    overdue: member.overdueCount,
  }));
}

function transformTaskProgress(
  response: TaskProgressApiResponse
): TaskChartDataPoint[] {
  return response.dataPoints.map((point) => ({
    date: point.date,
    pending: point.pending,
    completed: point.completed,
    inProgress: point.inProgress,
    delayed: point.delayed,
  }));
}

function transformRecentTasks(response: RecentTasksApiResponse): RecentTask[] {
  return response.tasks.map((task) => ({
    id: task.id,
    title: task.title,
    status: task.status as TaskStatus,
    assigneeName: task.assigneeName ?? null,
    assigneeAvatarUrl: task.assigneeAvatarUrl ?? null,
    dueDate: task.dueDate,
    priority: task.priority as Priority,
  }));
}

/** Transformed delayed tasks data */
export interface DelayedTasksData {
  readonly tasks: DelayedTask[];
  readonly totalCount: number;
}

function transformDelayedTasks(
  response: DelayedTasksApiResponse
): DelayedTasksData {
  return {
    tasks: response.tasks.map((task) => ({
      id: task.id,
      title: task.title,
      assigneeName: task.assigneeName ?? null,
      assigneeAvatarUrl: task.assigneeAvatarUrl ?? null,
      assignedToIsFormerMember: task.assignedToIsFormerMember,
      location: task.location ?? null,
      priority: task.priority as Priority,
      dueDate: task.dueDate,
      requirementCount: task.requirementCount,
      completedRequirementCount: task.completedRequirementCount,
    })),
    totalCount: response.totalCount,
  };
}

export const dashboardService = {
  /**
   * Get dashboard metrics for the current user
   */
  getMetrics: async (): Promise<DashboardMetricsData> => {
    const response = await apiClient.get<DashboardMetricsApiResponse>(
      API_ENDPOINTS.DASHBOARD.METRICS
    );
    return transformMetricsResponse(response.data);
  },

  /**
   * Get combined dashboard summary (stats + priorities + workload)
   */
  getSummary: async (): Promise<DashboardSummaryData> => {
    const response = await apiClient.get<DashboardSummaryApiResponse>(
      API_ENDPOINTS.DASHBOARD.SUMMARY
    );
    const data = response.data;
    return {
      taskStats: transformTaskStats(data.taskStats),
      priorityDistribution: transformPriorityDistribution(
        data.priorityDistribution.priorities
      ),
      totalTasks: data.priorityDistribution.totalTasks,
      teamWorkload: transformTeamWorkload(data.teamWorkload.members),
    };
  },

  /**
   * Get task progress over time (returns transformed domain types)
   */
  getTaskProgress: async (
    dateRange?: string
  ): Promise<TaskChartDataPoint[]> => {
    const params = dateRange ? { dateRange } : {};
    const response = await apiClient.get<TaskProgressApiResponse>(
      API_ENDPOINTS.DASHBOARD.TASK_PROGRESS,
      { params }
    );
    return transformTaskProgress(response.data);
  },

  getRecentTasks: async (): Promise<RecentTask[]> => {
    const response = await apiClient.get<RecentTasksApiResponse>(
      API_ENDPOINTS.DASHBOARD.RECENT_TASKS
    );
    return transformRecentTasks(response.data);
  },

  getDelayedTasks: async (): Promise<DelayedTasksData> => {
    const response = await apiClient.get<DelayedTasksApiResponse>(
      API_ENDPOINTS.DASHBOARD.DELAYED_TASKS
    );
    return transformDelayedTasks(response.data);
  },
};
